import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import BetaDashboard from "./BetaDashboard";
import TimeLogger from "./TimeLogger";
import Operator from "./Operator";
import OperatorBeta from "./OperatorBeta";
import BetaShell from "./BetaShell";
import UserManager from "./UserManager";
import DuplicateReport from "./DuplicateReport";
import AuditLog from "./AuditLog";
import PlanPage from "./PlanPage";
import PmView from "./PmView";
import { AuthProvider, useAuth } from "./AuthContext";
import LoginScreen from "./LoginScreen";

const API = "/api";
const GOLD = "#B8962E";

// ─── SUPPLIER ───
function LoginGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#888", fontFamily: "sans-serif" }}>Laadin...</div>;
  if (!user) return <LoginScreen />;
  return children;
}

function Supplier() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [company, setCompany] = useState("");
  const [reg, setReg] = useState("");
  const [material, setMaterial] = useState("");
  const [kogus, setKogus] = useState("");
  const [yhik, setYhik] = useState("t");
  const [marsruut, setMarsruut] = useState("");
  const [marsruutOptions, setMarsruutOptions] = useState([]);
  const regRef = useRef(null);
  const [objekt, setObjekt] = useState("");
  const [session, setSession] = useState(() => { try { const s = localStorage.getItem("supplier_session"); return s ? JSON.parse(s) : []; } catch { return []; } });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    localStorage.setItem("supplier_session", JSON.stringify(session));
  }, [session]);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    fetch(`${API}/sites`).then(r => r.json()).then(all => setSites(
      user.role === "admin" ? all : all.filter(s => user.sites.some(us => us.id === s.id))
    ));
    fetch(`${API}/materials`).then(r => r.json()).then(setMaterials);
    fetch(`${API}/transport-companies`).then(r => r.json()).then(setCompanies);
  }, [user]);

  const today = new Date().toISOString().split("T")[0];
  const canAdd = date && company && reg.trim().length >= 2 && material && kogus && objekt;

  const handleAdd = () => {
    if (!canAdd) return;
    const entry = { date, company, reg: reg.trim().toUpperCase(), material, kogus: parseFloat(kogus), yhik, objekt, marsruut: marsruut.trim(), id: Date.now(), addedAt: new Date().toISOString() };
    setSession(prev => [...prev, entry]);
    setReg(""); setKogus("");
    setTimeout(() => { if (regRef.current) regRef.current.focus(); }, 50);
    setFlash(true); setTimeout(() => setFlash(false), 600);
  };

  const handleRemove = (index) => setSession(prev => prev.filter((_, i) => i !== index));

  const handleSubmitAll = async () => {
    for (let i = 0; i < session.length; i++) {
      const entry = session[i];
      const r = await fetch(`${API}/supplier-deliveries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ delivery_date: entry.date, transport_company: entry.company, reg: entry.reg, site: entry.objekt, material: entry.material, kogus: entry.kogus, yhik: entry.yhik, marsruut: entry.marsruut, logged_at: entry.addedAt }),
      }).catch(() => null);
      if (!r || !r.ok) {
        // Keep unsaved entries so nothing is lost; saved ones are dropped to avoid duplicates on retry.
        setSession(session.slice(i));
        const detail = r ? (await r.json().catch(() => ({}))).detail : null;
        alert(`${i} lehte salvestatud. Salvestamine peatus: ${detail || "ühenduse viga"}. Ülejäänud lehed jäid nimekirja.`);
        return;
      }
    }
    localStorage.removeItem("supplier_session");
    setSubmitted(true);
  };

  const totalTonnes = session.filter(e => e.yhik === "t").reduce((s, e) => s + e.kogus, 0);
  const totalLoads = session.filter(e => e.yhik === "koorem").reduce((s, e) => s + e.kogus, 0);

  if (submitted) return (
    <div style={{ fontFamily: "sans-serif", background: "#f5f4f0", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, textAlign: "center" }}>
      <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#e8f5e9", border: "2px solid #1E6B2E", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, marginBottom: 20 }}>✓</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 6 }}>{session.length} lehte salvestatud</div>
      {totalTonnes > 0 && <div style={{ fontSize: 13, color: GOLD, fontWeight: 600 }}>{totalTonnes.toFixed(1)} t kokku</div>}
      {totalLoads > 0 && <div style={{ fontSize: 13, color: "#1a3a5c", fontWeight: 600 }}>{totalLoads} koormat kokku</div>}
      <button onClick={() => { setSession([]); setSubmitted(false); }} style={{ marginTop: 24, background: GOLD, border: "none", borderRadius: 8, padding: "12px 32px", fontWeight: 700, fontSize: 14, cursor: "pointer", color: "#fff" }}>Uus sessioon</button>
      <button onClick={() => navigate("/")} style={{ marginTop: 12, background: "none", border: "none", color: "#888", fontSize: 13, cursor: "pointer" }}>← Tagasi avalehele</button>
    </div>
  );

  return (
    <div style={{ fontFamily: "sans-serif", background: "#f5f4f0", minHeight: "100vh", color: "#1a1a14" }}>
      <div style={{ background: "#fff", borderBottom: `2px solid ${GOLD}`, padding: "14px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => navigate("/")} style={{ background: "none", border: "none", color: "#aaa", fontSize: 13, cursor: "pointer" }}>← Tagasi</button>
          <div style={{ background: GOLD, color: "#fff", fontWeight: 800, fontSize: 16, letterSpacing: 2, padding: "4px 10px", borderRadius: 5 }}>S</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: 1 }}>STEINMANN OÜ</div>
            <div style={{ fontSize: 10, color: "#aaa", letterSpacing: 2, textTransform: "uppercase" }}>Tarnelehtede sisestus</div>
          </div>
        </div>
        {session.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ background: flash ? "#fffbea" : "#f5f4f0", border: `1px solid ${flash ? GOLD : "#d8d5cc"}`, borderRadius: 8, padding: "8px 16px", transition: "all 0.3s" }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: GOLD }}>{session.length}</span>
              <span style={{ fontSize: 11, color: "#888", marginLeft: 6 }}>lehte</span>
            </div>
            <button onClick={handleSubmitAll} style={{ background: "#1E6B2E", border: "none", borderRadius: 8, padding: "9px 20px", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Salvesta kõik →</button>
          </div>
        )}
      </div>

      <div style={{ maxWidth: 780, margin: "0 auto", padding: "28px 24px" }}>
        <div style={{ background: "#fff", border: "1px solid #d8d5cc", borderRadius: 10, padding: 24, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 18 }}>📄 Sisesta tarneleht</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, color: "#888", letterSpacing: 1, textTransform: "uppercase", marginBottom: 5, fontWeight: 700 }}>Kuupäev</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} max={today}
                style={{ width: "100%", boxSizing: "border-box", background: "#fff", border: "1px solid #d8d5cc", borderRadius: 6, padding: "9px 12px", fontSize: 14, fontFamily: "sans-serif" }} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, color: "#888", letterSpacing: 1, textTransform: "uppercase", marginBottom: 5, fontWeight: 700 }}>Transpordiettevõte</label>
              <select value={company} onChange={e => setCompany(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box", background: "#fff", border: "1px solid #d8d5cc", borderRadius: 6, padding: "9px 12px", fontSize: 14, fontFamily: "sans-serif" }}>
                <option value="">— Vali ettevõte —</option>
                {companies.map(c => <option key={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, color: "#888", letterSpacing: 1, textTransform: "uppercase", marginBottom: 5, fontWeight: 700 }}>Auto reg. nr.</label>
              <input ref={regRef} value={reg} onChange={e => setReg(e.target.value.toUpperCase())} placeholder="nt. 306 TLH"
                style={{ width: "100%", boxSizing: "border-box", background: "#fff", border: "1px solid #d8d5cc", borderRadius: 6, padding: "9px 12px", fontSize: 14, fontFamily: "monospace", letterSpacing: 2, fontWeight: 700 }} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, color: "#888", letterSpacing: 1, textTransform: "uppercase", marginBottom: 5, fontWeight: 700 }}>Objekt</label>
              <select value={objekt} onChange={e => { setObjekt(e.target.value); fetch(`${API}/marsruut-suggestions?site=${encodeURIComponent(e.target.value)}`).then(r => r.json()).then(setMarsruutOptions); }}
                style={{ width: "100%", boxSizing: "border-box", background: "#fff", border: "1px solid #d8d5cc", borderRadius: 6, padding: "9px 12px", fontSize: 14, fontFamily: "sans-serif" }}>
                <option value="">— Vali objekt —</option>
                {sites.map(s => <option key={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 11, color: "#888", letterSpacing: 1, textTransform: "uppercase", marginBottom: 5, fontWeight: 700 }}>Marsruut</label>
            <input
              value={marsruut}
              onChange={e => setMarsruut(e.target.value)}
              list="marsruut-options"
              placeholder="nt. Reinu karjäär - Saku"
              style={{ width: "100%", boxSizing: "border-box", background: "#fff", border: "1px solid #d8d5cc", borderRadius: 6, padding: "9px 12px", fontSize: 14, fontFamily: "sans-serif" }}
            />
            <datalist id="marsruut-options">
              {marsruutOptions.map(opt => <option key={opt} value={opt} />)}
            </datalist>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 120px", gap: 16, marginBottom: 20 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, color: "#888", letterSpacing: 1, textTransform: "uppercase", marginBottom: 5, fontWeight: 700 }}>Materjal</label>
              <select value={material} onChange={e => setMaterial(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box", background: "#fff", border: "1px solid #d8d5cc", borderRadius: 6, padding: "9px 12px", fontSize: 14, fontFamily: "sans-serif" }}>
                <option value="">— Vali materjal —</option>
                {materials.map(m => <option key={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, color: "#888", letterSpacing: 1, textTransform: "uppercase", marginBottom: 5, fontWeight: 700 }}>Kogus</label>
              <input type="number" value={kogus} onChange={e => setKogus(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && canAdd) handleAdd(); }} placeholder="nt. 170.7" step="0.1"
                style={{ width: "100%", boxSizing: "border-box", background: "#fff", border: "1px solid #d8d5cc", borderRadius: 6, padding: "9px 12px", fontSize: 14, fontFamily: "sans-serif" }} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, color: "#888", letterSpacing: 1, textTransform: "uppercase", marginBottom: 5, fontWeight: 700 }}>Ühik</label>
              <div style={{ display: "flex", gap: 6, height: 38 }}>
                {["t", "koorem"].map(u => (
                  <button key={u} onClick={() => setYhik(u)} style={{ flex: 1, border: `1px solid ${yhik === u ? GOLD : "#d8d5cc"}`, borderRadius: 6, background: yhik === u ? "#fffbea" : "#fff", color: yhik === u ? GOLD : "#888", fontWeight: yhik === u ? 700 : 400, cursor: "pointer", fontSize: 13, fontFamily: "sans-serif" }}>{u}</button>
                ))}
              </div>
            </div>
          </div>
          <button onClick={handleAdd} disabled={!canAdd} style={{ width: "100%", padding: "12px", background: canAdd ? GOLD : "#f0ede8", border: `1px solid ${canAdd ? GOLD : "#d8d5cc"}`, borderRadius: 8, color: canAdd ? "#fff" : "#bbb", fontWeight: 700, fontSize: 14, cursor: canAdd ? "pointer" : "not-allowed", fontFamily: "sans-serif" }}>
            + Lisa leht nimekirja
          </button>
        </div>

        {session.length > 0 && (
          <div>
            <div style={{ fontSize: 11, color: "#aaa", letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>Selle sessiooni lehed</div>
            {session.map((entry, i) => (
              <div key={entry.id} style={{ background: "#fff", border: "1px solid #d8d5cc", borderLeft: `3px solid ${GOLD}`, borderRadius: 6, padding: "10px 14px", marginBottom: 6, display: "grid", gridTemplateColumns: "24px 1fr 1fr 1fr 1fr 1fr 1fr 28px", alignItems: "center", gap: 10, fontSize: 13 }}>
                <span style={{ color: "#bbb", fontSize: 11, fontWeight: 700 }}>{i + 1}</span>
                <span style={{ color: "#888", fontSize: 12 }}>{entry.date}</span>
                <span style={{ color: "#888", fontSize: 12 }}>{entry.addedAt ? new Date(entry.addedAt).toLocaleTimeString("et-EE", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                <span style={{ fontWeight: 700 }}>{entry.reg}</span>
                <span style={{ color: "#1a3a5c" }}>{entry.material}</span>
                <span style={{ fontWeight: 700, color: GOLD }}>{entry.kogus} {entry.yhik}</span>
                <span style={{ color: "#888", fontSize: 12 }}>{entry.marsruut || entry.objekt}</span>
                <button onClick={() => handleRemove(i)} style={{ background: "none", border: "none", color: "#ccc", cursor: "pointer", fontSize: 16, padding: 0 }}>✕</button>
              </div>
            ))}
            <div style={{ marginTop: 12, padding: "12px 16px", background: "#fff", border: "1px solid #d8d5cc", borderRadius: 8, display: "flex", flexWrap: "wrap", gap: 16 }}>
              {Object.entries(session.reduce((acc, e) => {
                const key = `${e.material}__${e.yhik}`;
                acc[key] = (acc[key] || 0) + e.kogus;
                return acc;
              }, {})).map(([key, total]) => {
                const [mat, unit] = key.split("__");
                return <div key={key}><span style={{ fontWeight: 700, color: "#1a1a14" }}>{mat}:</span> <span style={{ color: GOLD, fontWeight: 700 }}>{unit === "t" ? total.toFixed(1) : total}</span> <span style={{ color: "#888", fontSize: 12 }}>{unit}</span></div>;
              })}
            </div>
            <button onClick={handleSubmitAll} style={{ width: "100%", marginTop: 12, padding: "13px", background: "#1E6B2E", border: "none", borderRadius: 8, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "sans-serif" }}>
              Salvesta kõik {session.length} lehte →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── ROOT ───
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<BetaShell />}>
          <Route index element={<BetaDashboard hiddenModules={["paevaaruanded", "tarnelehed"]} showDupFlags />} />
          <Route path="operator" element={<Operator />} />
          <Route path="time" element={<TimeLogger />} />
          <Route path="users" element={<UserManager />} />
          <Route path="duplicates" element={<DuplicateReport />} />
          <Route path="audit" element={<AuditLog />} />
        </Route>
        <Route path="/supplier" element={<AuthProvider><LoginGate><Supplier /></LoginGate></AuthProvider>} />
        <Route path="/beta" element={<BetaShell />}>
          <Route index element={<BetaDashboard hiddenModules={["paevaaruanded", "tarnelehed"]} showDupFlags />} />
          <Route path="operator" element={<OperatorBeta />} />
          <Route path="time" element={<TimeLogger />} />
          <Route path="users" element={<UserManager />} />
          <Route path="duplicates" element={<DuplicateReport />} />
          <Route path="audit" element={<AuditLog />} />
          <Route path="plan" element={<PlanPage />} />
          <Route path="pm" element={<PmView />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
